import React, { useState, useEffect, useRef, ReactNode, useMemo } from 'react';
import { FormControl, InputLabel, Box } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAnimatedScroll } from '@core/hooks/useAnimatedScroll';
import VirtualScrollList, { VirtualScrollListRef } from '@/components/shared/VirtualScrollList.tsx';
import WcnWarningIcon from '@/components/shared/Icon/WcnWarningIcon.tsx';

export interface SelectOption {
    [key: string]: any;
}

export interface SelectProps {
    value?: any;
    items: SelectOption[];
    itemText?: string;
    itemValue?: string;
    multiple?: boolean;
    selectAll?: boolean;
    selectAllLabel?: string;
    disabled?: boolean;
    label?: string;
    hideLabel?: boolean;
    rules?: Array<(value: any) => string | boolean>;
    onChange?: (value: any) => void;
    onBlur?: () => void;
    className?: string;
    sx?: any;
    error?: boolean;
    helperText?: string;
    noDataText?: string;
    placeholder?: string;
    searchable?: boolean;
    renderDisplayValue?: (selectedValues: any[], items: SelectOption[]) => ReactNode;
    // Optional custom renderers for the single-select path only. When omitted, behavior is unchanged (plain text).
    renderSelectedValue?: (item: SelectOption) => ReactNode;
    renderOption?: (item: SelectOption, isSelected: boolean) => ReactNode;
    virtual?: boolean;
    itemHeight?: number;
    virtualListHeight?: number;
    clearable?: boolean;
    onSelection?: (meta: { allSelected: boolean; isIndeterminate: boolean }) => void;
    selectDisplayLabels?: { allLabel?: string; placeholderLabel?: string; countLabel?: string };
    warning?: boolean;
    warningText?: string;
    required?: boolean;
}

const AUTO_VIRTUAL_THRESHOLD = 50;

// ─── Icons ────────────────────────────────────────────────────────────────────

const CheckboxIcon = ({ checked, indeterminate }: { checked: boolean; indeterminate?: boolean }) => (
    <span className={`multi-select-checkbox ${checked ? 'checked' : ''}`}>
        {checked && <CheckIcon className="multi-select-checkbox-check" />}
        {indeterminate && !checked && <span className="multi-select-checkbox-indeterminate" />}
    </span>
);

const FoldIcon = () => (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 6L5 2L9 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path
            d="M1 10L5 14L9 10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

const BaseSelector = ({
    value,
    items = [],
    itemText = 'text',
    itemValue = 'value',
    noDataText = 'noData',
    multiple = false,
    selectAll = false,
    disabled = false,
    searchable = false,
    label,
    hideLabel = false,
    className = '',
    sx,
    rules = [],
    error: externalError,
    helperText: externalHelperText,
    onChange,
    onBlur,
    renderSelectedValue,
    renderOption,
    virtual,
    itemHeight = 40,
    virtualListHeight = 300,
    clearable = false,
    onSelection,
    selectDisplayLabels,
    placeholder,
    warning = false,
    warningText = '',
    required = false
}: SelectProps) => {
    const [internalError, setInternalError] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    // Single select scroll refs
    const singleMenuRef = useRef<HTMLDivElement>(null);
    const singleVirtualRef = useRef<VirtualScrollListRef>(null);

    // Multi select scroll refs
    const multiMenuRef = useRef<HTMLDivElement>(null);
    const multiVirtualRef = useRef<VirtualScrollListRef>(null);

    const { t } = useTranslation();

    const useVirtual = virtual !== undefined ? virtual : items.length > AUTO_VIRTUAL_THRESHOLD;

    const getItemText = (item: any) => item[itemText];
    const getItemValue = (item: any) => item[itemValue];

    const valueMap = useMemo(() => {
        const map = new Map<any, { item: SelectOption; index: number }>();
        items.forEach((item, index) => {
            map.set(getItemValue(item), { item, index });
        });
        return map;
    }, [items]);

    // Validation
    useEffect(() => {
        if (rules.length > 0 && value !== null && value !== undefined) {
            for (const rule of rules) {
                const result = rule(value);
                if (result !== true) {
                    setInternalError(typeof result === 'string' ? result : 'Invalid value');
                    return;
                }
            }
            setInternalError('');
        }
    }, [value, rules]);

    const hasError = externalError !== undefined ? externalError : !!internalError;
    const errorMessage = externalHelperText !== undefined ? externalHelperText : internalError;

    const filteredItems = useMemo(
        () =>
            searchable && searchText
                ? items.filter((item) =>
                      String(getItemText(item) ?? '')
                          .toLowerCase()
                          .includes(searchText.toLowerCase())
                  )
                : items,
        [items, searchText, searchable]
    );

    useEffect(() => {
        if (!isOpen) return;
        window.addEventListener('blur', handleClose);
        return () => window.removeEventListener('blur', handleClose);
    }, [isOpen]);

    // ── Scroll hooks ──────────────────────────────────────────────────────────

    const singleScroll = useAnimatedScroll({
        msPerItem: 100,
        minDuration: 700,
        itemHeight: useVirtual ? itemHeight : undefined
    });

    useEffect(() => {
        if (!isOpen || multiple) return;
        let rafId: number;
        const doScroll = () => {
            if (useVirtual) {
                const idx = filteredItems.findIndex((item) => getItemValue(item) === value);
                if (idx !== -1) {
                    singleScroll.scrollToIndexFast(singleVirtualRef.current?.getContainer() ?? null, idx, itemHeight, {
                        preJumpItems: 5
                    });
                }
            } else {
                singleScroll.scrollToSelectorFast(singleMenuRef.current, '.single-selected', { preJumpItems: 5 });
            }
        };
        rafId = requestAnimationFrame(() => {
            rafId = requestAnimationFrame(doScroll);
        });
        return () => cancelAnimationFrame(rafId);
    }, [isOpen]);

    // Click-outside handle
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                handleClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleOpen = () => {
        if (disabled) return;
        setIsOpen(true);
        setSearchText('');
    };

    const handleClose = () => {
        setIsOpen(false);
        setSearchText('');
        onBlur?.();
    };

    // Shared menu wrapper
    const renderMenu = (options: ReactNode, listRef?: React.RefObject<HTMLDivElement | null>, extraClass?: string) => (
        <Box
            ref={listRef}
            className={[searchable ? 'selector-menu searchable-open' : 'selector-menu non-searchable-open', extraClass]
                .filter(Boolean)
                .join(' ')}
            onMouseDown={(e) => {
                if ((e.target as HTMLElement).closest('.selector-search-field')) return;
                e.preventDefault();
            }}
        >
            <span className="selector-icon selector-icon--inside-menu" onClick={handleClose}>
                <FoldIcon />
            </span>
            {searchable && (
                <Box className="selector-search-input">
                    <input
                        autoFocus
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={t('typeToSearch')}
                        className="selector-search-field"
                    />
                </Box>
            )}
            {options}
        </Box>
    );

    // ── SINGLE SELECT ─────────────────────────────────────────────────────────

    const selectedSingleOption = value !== null && value !== undefined ? (valueMap.get(value)?.item ?? null) : null;

    const singleDisplayValue = useMemo(() => {
        return selectedSingleOption ? (
            renderSelectedValue ? (
                renderSelectedValue(selectedSingleOption)
            ) : (
                <span className="selector-selected-value">{getItemText(selectedSingleOption)}</span>
            )
        ) : (
            <span className="selector-placeholder">
                {t(selectDisplayLabels?.placeholderLabel ?? placeholder ?? 'selectAOption')}
            </span>
        );
    }, [t, selectedSingleOption, placeholder, selectDisplayLabels, renderSelectedValue]);

    const renderSingleOptions = () => {
        if (filteredItems.length === 0) {
            return <Box className="selector-no-options">{t(noDataText)}</Box>;
        }

        if (useVirtual) {
            return (
                <VirtualScrollList
                    ref={singleVirtualRef}
                    items={filteredItems}
                    itemHeight={itemHeight}
                    height={virtualListHeight}
                    overscan={5}
                    renderItem={(item) => {
                        const itemVal = getItemValue(item);
                        const isSelected = itemVal === value;
                        const isDisabled = !!item.disabled;
                        return (
                            <Box
                                className={`selector-option ${isSelected ? 'selected single-selected' : ''}${isDisabled ? ' disabled' : ''}`}
                                style={{ height: itemHeight, boxSizing: 'border-box' }}
                                onClick={() => {
                                    if (isDisabled) return;
                                    onChange?.(itemVal);
                                    setSearchText('');
                                    handleClose();
                                }}
                            >
                                {isSelected && <CheckIcon className="single-select-check-icon" />}
                                {renderOption ? renderOption(item, isSelected) : <span>{getItemText(item)}</span>}
                            </Box>
                        );
                    }}
                />
            );
        }

        return (
            <>
                {filteredItems.map((item) => {
                    const itemVal = getItemValue(item);
                    const isSelected = itemVal === value;
                    const isDisabled = !!item.disabled;
                    return (
                        <Box
                            key={itemVal}
                            className={`selector-option ${isSelected ? 'selected single-selected' : ''}${isDisabled ? ' disabled' : ''}`}
                            onClick={() => {
                                if (isDisabled) return;
                                onChange?.(itemVal);
                                setSearchText('');
                                handleClose();
                            }}
                        >
                            {isSelected && <CheckIcon className="single-select-check-icon" />}
                            {renderOption ? renderOption(item, isSelected) : <span>{getItemText(item)}</span>}
                        </Box>
                    );
                })}
            </>
        );
    };

    // ── MULTI SELECT ──────────────────────────────────────────────────────────

    const selectedValues: any[] = Array.isArray(value) ? value : [];

    const allItemValues = useMemo(() => (multiple ? items.map(getItemValue) : []), [items, multiple]);

    const allSelected = allItemValues.length > 0 && allItemValues.every((v) => selectedValues.includes(v));
    const isIndeterminate = selectedValues.length > 0 && !allSelected;

    useEffect(() => {
        if (multiple) {
            onSelection?.({ allSelected, isIndeterminate });
        }
    }, [allSelected, isIndeterminate]);

    const handleMultiToggle = (itemVal: any) => {
        if (selectedValues.includes(itemVal)) {
            onChange?.(selectedValues.filter((v) => v !== itemVal));
        } else {
            onChange?.([...selectedValues, itemVal]);
        }
    };

    const multiDisplayLabel = useMemo(() => {
        if (selectedValues.length === 0) return null;

        return selectedValues
            .map((v) => valueMap.get(v))
            .filter(Boolean)
            .sort((a, b) => a!.index - b!.index)
            .map((entry) => getItemText(entry!.item))
            .join('; ');
    }, [selectedValues, valueMap]);

    const multiDisplayValue = useMemo(() => {
        const { allLabel, placeholderLabel, countLabel } = selectDisplayLabels ?? {
            placeholderLabel: placeholder ?? 'selectOptions'
        };
        if (allSelected && allLabel) return <span className="selector-selected-value">{allLabel}</span>;
        if (selectedValues.length === 0)
            return <span className="selector-placeholder">{placeholderLabel ?? t(noDataText)}</span>;

        return countLabel && selectedValues.length > 1 ? (
            <span className="selector-selected-value">
                {countLabel.replace('{0}', selectedValues.length.toString())}
            </span>
        ) : (
            <span className="selector-selected-value">{multiDisplayLabel}</span>
        );
    }, [selectDisplayLabels, allSelected, selectedValues, multiDisplayLabel]);

    const renderMultiOptions = () => {
        const isEmpty = filteredItems.length === 0;

        const selectAllRow = selectAll ? (
            <Box
                className={`selector-option multi-select-option ${allSelected ? 'selected' : ''}`}
                onClick={() => onChange?.(allSelected ? [] : allItemValues)}
                style={{ fontWeight: 600 }}
            >
                <CheckboxIcon checked={allSelected} indeterminate={isIndeterminate} />
                <span>{selectDisplayLabels?.allLabel ?? t('selectAll')}</span>
            </Box>
        ) : null;

        if (isEmpty) {
            return (
                <>
                    {selectAllRow}
                    <Box className="selector-no-options">{t(noDataText)}</Box>
                </>
            );
        }

        if (useVirtual) {
            const listHeight = selectAll ? virtualListHeight - itemHeight : virtualListHeight;

            return (
                <>
                    {selectAllRow}
                    <VirtualScrollList
                        ref={multiVirtualRef}
                        items={filteredItems}
                        itemHeight={itemHeight}
                        height={Math.max(listHeight, itemHeight * 2)}
                        overscan={5}
                        renderItem={(item) => {
                            const itemVal = getItemValue(item);
                            const isSelected = selectedValues.includes(itemVal);
                            const isDisabled = !!item.disabled;
                            return (
                                <Box
                                    className={`selector-option multi-select-option${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                                    style={{ height: itemHeight, boxSizing: 'border-box' }}
                                    onClick={() => {
                                        if (isDisabled) return;
                                        handleMultiToggle(itemVal);
                                    }}
                                >
                                    <CheckboxIcon checked={isSelected} />
                                    <span>{getItemText(item)}</span>
                                </Box>
                            );
                        }}
                    />
                </>
            );
        }

        return (
            <>
                {selectAllRow}
                {filteredItems.map((item) => {
                    const itemVal = getItemValue(item);
                    const isSelected = selectedValues.includes(itemVal);
                    const isDisabled = !!item.disabled;
                    return (
                        <Box
                            key={itemVal}
                            className={`selector-option multi-select-option${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                            onClick={() => {
                                if (isDisabled) return;
                                handleMultiToggle(itemVal);
                            }}
                        >
                            <CheckboxIcon checked={isSelected} />
                            <span>{getItemText(item)}</span>
                        </Box>
                    );
                })}
            </>
        );
    };

    const outerWrapperStyle = disabled ? { cursor: 'not-allowed' } : {};
    const innerWrapperStyle = disabled ? { pointerEvents: 'none' as const } : {};

    // ── Shared warning footer (used by both single and multi renders) ──────────
    const showWarning = warning && !hasError;
    const warningFooter = showWarning ? (
        <Box className="selector-helper wcn-input-helper--warning">{warningText}</Box>
    ) : null;

    // RENDER: MULTI SELECT
    if (multiple) {
        return (
            <Box className={`selector-wrapper ${className}`} ref={containerRef} sx={sx} style={outerWrapperStyle}>
                <FormControl
                    fullWidth
                    disabled={disabled}
                    error={hasError}
                    className={`selector-container ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                >
                    {!hideLabel && label && (
                        <InputLabel shrink className="selector-label" required={required}>
                            {label}
                        </InputLabel>
                    )}
                    <Box className="selector-input-wrapper" style={innerWrapperStyle}>
                        <Box
                            className={`selector-input-box ${hasError ? 'error' : ''}`}
                            onClick={disabled ? undefined : handleOpen}
                            style={{ visibility: isOpen && searchable ? 'hidden' : 'visible' }}
                            tabIndex={disabled ? -1 : 0}
                        >
                            {multiDisplayValue}
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {selectedValues.length > 0 && (
                                    <span
                                        className="selector-clear-btn"
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: 22,
                                            lineHeight: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginTop: 2.5,
                                            color: 'var(--gray-medium)'
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onChange?.([]);
                                        }}
                                    >
                                        ×
                                    </span>
                                )}
                                <span className="selector-icon" style={{ display: 'flex', alignItems: 'center' }}>
                                    <FoldIcon />
                                </span>
                            </span>
                        </Box>

                        {isOpen &&
                            renderMenu(
                                renderMultiOptions(),
                                useVirtual ? undefined : multiMenuRef,
                                useVirtual ? 'virtual-mode' : undefined
                            )}

                        {showWarning && <WcnWarningIcon />}
                    </Box>
                    {errorMessage && <Box className={`selector-helper ${hasError ? 'error' : ''}`}>{errorMessage}</Box>}
                    {warningFooter}
                </FormControl>
            </Box>
        );
    }

    // RENDER: SINGLE SELECT
    return (
        <Box className={`selector-wrapper ${className}`} ref={containerRef} sx={sx} style={outerWrapperStyle}>
            <FormControl
                fullWidth
                disabled={disabled}
                error={hasError}
                className={`selector-container ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
            >
                {!hideLabel && label && (
                    <InputLabel shrink className="selector-label" required={required}>
                        {label}
                    </InputLabel>
                )}
                <Box className="selector-input-wrapper" style={innerWrapperStyle}>
                    <Box
                        className={`selector-input-box ${hasError ? 'error' : ''}`}
                        onClick={disabled ? undefined : handleOpen}
                        style={{ visibility: isOpen && searchable ? 'hidden' : 'visible' }}
                        tabIndex={disabled ? -1 : 0}
                    >
                        {singleDisplayValue}
                        <span className="selector-icon">
                            {clearable && selectedSingleOption != null && (
                                <span
                                    className="selector-clear-btn"
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: 22,
                                        lineHeight: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginTop: 2.5
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onChange?.(null);
                                    }}
                                >
                                    ×
                                </span>
                            )}
                            <span className="selector-icon" style={{ display: 'flex', alignItems: 'center' }}>
                                <FoldIcon />
                            </span>
                        </span>
                    </Box>

                    {isOpen &&
                        renderMenu(
                            renderSingleOptions(),
                            useVirtual ? undefined : singleMenuRef,
                            useVirtual ? 'virtual-mode' : undefined
                        )}

                    {showWarning && <WcnWarningIcon />}
                </Box>
                {errorMessage && <Box className={`selector-helper ${hasError ? 'error' : ''}`}>{errorMessage}</Box>}
                {warningFooter}
            </FormControl>
        </Box>
    );
};

export default BaseSelector;
